import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, CheckCircle, Circle, Camera, FloppyDisk, ClockClockwise, X, Microphone, Stop, Play, Pause } from "@phosphor-icons/react";
import { motion } from "framer-motion";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { istToday } from "../lib/day";

const VOICE_MAX_SEC = 30;
const MON_FULL = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

// "14:30" (24h) → "2:30 PM"; passes through anything it can't parse.
function fmtTime(t) {
  if (!t) return "";
  const [h, m] = String(t).split(":").map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return t;
  const ap = h < 12 ? "AM" : "PM";
  const hh = ((h + 11) % 12) + 1;
  return `${hh}:${String(m).padStart(2, "0")} ${ap}`;
}

// IST date+month and hour:minute, spoken before the voice note plays.
function voiceStampIST() {
  const d = new Date(Date.now() + 19800000);
  let h = d.getUTCHours(); const ap = h >= 12 ? "PM" : "AM"; h = h % 12 || 12;
  return `${d.getUTCDate()} ${MON_FULL[d.getUTCMonth()]}, ${h}:${String(d.getUTCMinutes()).padStart(2, "0")} ${ap}`;
}
const blobToB64 = (blob) => new Promise((res) => { const r = new FileReader(); r.onload = () => res(String(r.result).split(",")[1] || ""); r.readAsDataURL(blob); });

function fileToB64(file) {
  return new Promise((resolve) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      const w = Math.min(720, img.width || 720);
      const ratio = w / (img.width || w);
      const h = Math.round((img.height || 540) * ratio);
      const c = document.createElement("canvas"); c.width = w; c.height = h;
      c.getContext("2d").drawImage(img, 0, 0, w, h);
      URL.revokeObjectURL(url);
      resolve((c.toDataURL("image/jpeg", 0.6).split(",")[1]) || "");
    };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(""); };
    img.src = url;
  });
}

export default function DailyChecklist() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const today = istToday(); // IST, not UTC — see lib/day.js
  const [date, setDate] = useState(today);
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({ updatedAt: null, updatedByName: null, canEdit: false });
  const [locked, setLocked] = useState(() => new Set()); // item ids already marked done (immutable)
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [viewer, setViewer] = useState(null); // full-screen photo preview
  const [recIndex, setRecIndex] = useState(null);
  const [recLeft, setRecLeft] = useState(VOICE_MAX_SEC);
  const [playingId, setPlayingId] = useState(null);
  const mediaRec = useRef(null); const chunks = useRef([]); const recTimer = useRef(null);
  const recStamp = useRef(""); const audioEl = useRef(null);

  function stopPlayback() {
    try { window.speechSynthesis?.cancel(); } catch {}
    try { if (audioEl.current) { audioEl.current.pause(); audioEl.current = null; } } catch {}
    setPlayingId(null);
  }
  useEffect(() => () => { if (recTimer.current) clearInterval(recTimer.current); stopPlayback(); }, []);

  async function startVoice(i) {
    if (recIndex !== null) return;
    stopPlayback();
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunks.current = [];
      recStamp.current = voiceStampIST();
      mr.ondataavailable = (e) => { if (e.data?.size) chunks.current.push(e.data); };
      mr.onstop = async () => {
        try { stream.getTracks().forEach((t) => t.stop()); } catch {}
        const blob = new Blob(chunks.current, { type: mr.mimeType || "audio/webm" });
        const b64 = await blobToB64(blob);
        if (b64) { setItems((a) => a.map((x, j) => (j === i ? { ...x, voiceBase64: b64, voiceMime: blob.type, voiceAt: recStamp.current } : x))); setDirty(true); }
      };
      mediaRec.current = mr; mr.start();
      setRecIndex(i); setRecLeft(VOICE_MAX_SEC);
      recTimer.current = setInterval(() => setRecLeft((s) => { if (s <= 1) { stopVoice(); return 0; } return s - 1; }), 1000);
    } catch { /* mic denied */ }
  }
  function stopVoice() {
    if (recTimer.current) { clearInterval(recTimer.current); recTimer.current = null; }
    try { mediaRec.current && mediaRec.current.state !== "inactive" && mediaRec.current.stop(); } catch {}
    setRecIndex(null);
  }
  function playVoice(it) {
    if (playingId === it.id) { stopPlayback(); return; }
    stopPlayback();
    setPlayingId(it.id);
    const playAudio = () => {
      try {
        const a = new Audio(`data:${it.voiceMime || "audio/webm"};base64,${it.voiceBase64}`);
        a.onended = () => setPlayingId(null);
        audioEl.current = a; a.play();
      } catch { setPlayingId(null); }
    };
    try {
      const u = new SpeechSynthesisUtterance(it.voiceAt || "");
      u.onend = playAudio; u.onerror = playAudio;
      window.speechSynthesis.speak(u);
    } catch { playAudio(); }
  }

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get(`/api/checklist?date=${date}`);
      const its = data.items || [];
      setItems(its);
      setLocked(new Set(its.filter((i) => i.done).map((i) => i.id)));
      setMeta({ updatedAt: data.updatedAt, updatedByName: data.updatedByName, canEdit: !!data.canEdit });
      setDirty(false);
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [date]);

  const editable = meta.canEdit;
  const doneCount = items.filter((i) => i.done).length;
  const pct = items.length ? Math.round((doneCount / items.length) * 100) : 0;

  // Once an item is marked done it's locked (can't revert); only today is editable.
  function toggle(i) {
    if (!editable) return;
    const it = items[i];
    if (locked.has(it.id)) return;
    setItems((a) => a.map((x, j) => (j === i ? { ...x, done: !x.done } : x)));
    setDirty(true);
  }
  function setRemark(i, v) { if (locked.has(items[i].id)) return; setItems((a) => a.map((it, j) => (j === i ? { ...it, remarks: v } : it))); setDirty(true); }
  async function onPhoto(i, e) {
    const f = e.target.files?.[0]; if (e.target) e.target.value = "";
    if (!f) return;
    const b64 = await fileToB64(f);
    setItems((a) => a.map((it, j) => (j === i ? { ...it, photoBase64: b64 } : it))); setDirty(true);
  }
  function removePhoto(i) { setItems((a) => a.map((it, j) => (j === i ? { ...it, photoBase64: "" } : it))); setDirty(true); }
  async function save() {
    setSaving(true);
    try {
      const { data } = await api.put(`/api/checklist?date=${date}`, { items });
      setMeta({ updatedAt: data.updatedAt, updatedByName: data.updatedByName, canEdit: true });
      setDirty(false);
    } catch { /* ignore */ } finally { setSaving(false); }
  }

  return (
    <div className="font-sans pb-4">
      {/* Frozen header: heading + Save + date/progress stay put while items scroll */}
      <div className="sticky top-0 z-20 -mx-4 border-b border-white/5 bg-[#0a0a0b]/95 px-4 pb-3 backdrop-blur-xl" style={{ paddingTop: "max(0.25rem, env(safe-area-inset-top))" }}>
        <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-white/70 hover:text-white">
          <ArrowLeft size={16} weight="bold" /> Back
        </button>
        <div className="mt-2 flex items-center justify-between gap-2">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.32em] text-white/50">Operations</p>
            <h1 className="font-display text-2xl font-extrabold text-white">Daily Checklist</h1>
          </div>
          {editable && (
            <button onClick={save} disabled={saving || !dirty}
              className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-neon-cyan px-4 py-2 text-[12px] font-bold text-black shadow-glow-cyan transition-all disabled:bg-white/10 disabled:text-white/40 disabled:shadow-none">
              <FloppyDisk size={14} weight="bold" /> {saving ? "Saving…" : "Save"}
            </button>
          )}
        </div>
        {/* Date + progress */}
        <div className="card mt-3 p-3">
          <div className="flex items-center justify-between gap-2">
            <input type="date" max={today} value={date} onChange={(e) => setDate(e.target.value)} className="field !w-auto !py-2 text-[13px]" />
            <span className="font-mono text-[13px] font-bold text-neon-cyan">{doneCount} of {items.length}</span>
          </div>
          <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-white/10">
            <motion.div className="h-full rounded-full bg-gradient-to-r from-neon-cyan to-neon-lime" animate={{ width: `${pct}%` }} transition={{ duration: 0.4 }} />
          </div>
          <p className="mt-2 text-[11px] text-white/45">
            {meta.updatedAt ? <><ClockClockwise size={11} weight="bold" className="-mt-0.5 mr-1 inline" />Last updated {new Date(meta.updatedAt).toLocaleString()}{meta.updatedByName ? ` · ${meta.updatedByName}` : ""}</> : "Not updated yet"}
            {!editable && <span className="ml-1 text-neon-amber">· read-only</span>}
          </p>
        </div>
      </div>

      {loading ? (
        <div className="card mt-3 p-8 text-center text-sm text-white/55">Loading…</div>
      ) : (
        <div className="mt-3 space-y-2">
          {items.map((it, i) => (
            <div key={it.id} className={`card p-3.5 ${it.done ? "border-neon-lime/30" : ""}`}>
              <div className="flex items-start gap-3">
                <button onClick={() => toggle(i)} disabled={!editable || locked.has(it.id)}
                  className="mt-0.5 shrink-0 disabled:opacity-100">
                  {it.done
                    ? <CheckCircle size={24} weight="fill" className="text-neon-lime" />
                    : <Circle size={24} weight="bold" className="text-white/30" />}
                </button>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="rounded-md bg-white/10 px-1.5 py-0.5 font-mono text-[10px] font-bold text-white/70">{fmtTime(it.time)}</span>
                    <span className={`text-[10px] font-bold uppercase tracking-wider ${it.done ? "text-neon-lime" : "text-white/40"}`}>{it.done ? "Done" : "Not done"}</span>
                    {it.done && it.doneAt && (
                      <span className="text-[10px] text-white/40">· {new Date(it.doneAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
                    )}
                  </div>
                  <p className={`mt-1 text-[14px] font-semibold ${it.done ? "text-white" : "text-white/80"}`}>{it.task}</p>

                  {/* remarks */}
                  {editable && !locked.has(it.id) ? (
                    <input value={it.remarks || ""} onChange={(e) => setRemark(i, e.target.value)} placeholder="Add remark (optional)"
                      className="mt-2 w-full rounded-lg border border-white/10 bg-white/[0.03] px-2.5 py-1.5 text-[12px] text-white outline-none placeholder:text-white/30 focus:border-neon-cyan" />
                  ) : it.remarks ? (
                    <p className="mt-1.5 text-[12px] text-white/55">“{it.remarks}”</p>
                  ) : null}

                  {/* photo + voice */}
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    {it.photoBase64 ? (
                      <div className="relative">
                        <img src={`data:image/jpeg;base64,${it.photoBase64}`} alt="" onClick={() => setViewer(`data:image/jpeg;base64,${it.photoBase64}`)} className="h-14 w-14 cursor-zoom-in rounded-lg object-cover" />
                        {editable && !locked.has(it.id) && <button onClick={() => removePhoto(i)} className="absolute -right-1.5 -top-1.5 rounded-full bg-neon-pink p-0.5 text-white"><X size={10} weight="bold" /></button>}
                      </div>
                    ) : editable && !locked.has(it.id) ? (
                      <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-[11px] font-semibold text-white/70 hover:bg-white/10">
                        <Camera size={14} weight="bold" /> Photo
                        <input type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => onPhoto(i, e)} />
                      </label>
                    ) : null}

                    {/* voice note: record (<=30s) on editable items; play (with spoken IST timestamp) anywhere */}
                    {editable && !locked.has(it.id) && recIndex === i ? (
                      <button onClick={stopVoice} className="inline-flex items-center gap-1.5 rounded-full border border-neon-pink/40 bg-neon-pink/10 px-3 py-1.5 text-[11px] font-bold text-neon-pink">
                        <Stop size={13} weight="fill" /> Recording {recLeft}s · stop
                      </button>
                    ) : it.voiceBase64 ? (
                      <button onClick={() => playVoice(it)} className="inline-flex items-center gap-1.5 rounded-full border border-neon-lime/40 bg-neon-lime/10 px-3 py-1.5 text-[11px] font-bold text-neon-lime">
                        {playingId === it.id ? <Pause size={13} weight="fill" /> : <Play size={13} weight="fill" />}
                        {playingId === it.id ? "Playing…" : (editable && !locked.has(it.id) ? "Voice added" : `Voice note${it.voiceAt ? ` · ${it.voiceAt}` : ""}`)}
                      </button>
                    ) : editable && !locked.has(it.id) ? (
                      <button onClick={() => startVoice(i)} disabled={recIndex !== null} className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-[11px] font-semibold text-white/70 hover:bg-white/10 disabled:opacity-40">
                        <Microphone size={14} weight="bold" /> Record voice
                      </button>
                    ) : null}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {viewer && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/90 p-4" onClick={() => setViewer(null)}>
          <img src={viewer} alt="" className="max-h-[88vh] max-w-full rounded-xl object-contain" />
          <button onClick={() => setViewer(null)} className="absolute right-5 top-5 rounded-full bg-white/10 p-2 text-white"><X size={20} weight="bold" /></button>
        </div>
      )}
    </div>
  );
}
